import { ValidationRules } from '.';
import { MixedValidationContrans } from './types/mixed';
import { StringValidationContrans } from './types/string';
import { validationRulesBuilder } from './helpers/validationRulesBuilder';

/**
 * ### String Validations Rules Builder
 *
 * @author <pino0071@gmail.com> Jose Aburto
 */
export class StringRulesBuilder {
  private constrains: StringValidationContrans[] = [];

  private constructor() {
    this.constrains = [];
  }

  /**
   * Use it if you want to validate a string is present always.
   */
  public required(message: string): Omit<StringRulesBuilder, 'required'> {
    this.constrains.push({ method: 'required', params: { message } });
    return this;
  }

  public email(message: string): Omit<StringRulesBuilder, 'email'> {
    this.constrains.push({ method: 'email', params: { message } });
    return this;
  }

  /**
   * Use it if you want to validate a password to be strong
   */
  public securePassword(message: string): Omit<StringRulesBuilder, 'securePassword'> {
    this.constrains.push({
      method: 'matches',
      params: { value: /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/, message },
    });
    return this;
  }

  public validCaptcha(message: string): Omit<StringRulesBuilder, 'validCaptcha'> {
    this.constrains.push({ method: 'min', params: { value: 10, message } });
    return this;
  }

  /**
   * If you are done, just use this to get
   * all the validation rules configuration and
   * use it into you controller.
   *
   * @example
   * {
   *  type: 'password'
   *  name : 'mypassword',
   *  validationRules: StringRulesBuilder.new().required('message').get(),
   * }
   */
  public get(): ValidationRules {
    return validationRulesBuilder({ type: 'string', constrains: this.constrains });
  }

  public static new(): StringRulesBuilder {
    return new StringRulesBuilder();
  }
}

/**
 * ### Mixed Validations Rules Builder
 *
 * @author <pino0071@gmail.com> Jose Aburto
 */
export class MixedRulesBuilder {
  private constrains: MixedValidationContrans[] = [];

  private constructor() {
    this.constrains = [];
  }

  /**
   * Use it if you want to validate a range of values not included
   */
  public notOneOf(message: string, value: any[]): Omit<MixedRulesBuilder, 'notOneOf'> {
    this.constrains.push({ method: 'notOneOf', params: { value, message } });
    return this;
  }

  /**
   * If you are done, just use this to get
   * all the validation rules configuration and
   * use it into you controller.
   *
   * @example
   * {
   *  type: 'password'
   *  name : 'mypassword',
   *  validationRules: MixedRulesBuilder.new().required('message').get(),
   * }
   */
  public get(): ValidationRules {
    return validationRulesBuilder({ type: 'mixed', constrains: this.constrains });
  }

  public static new(): MixedRulesBuilder {
    return new MixedRulesBuilder();
  }
}
